import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Phone, Lock, DotsThreeVertical, X, PhoneCall, ChatCircle, Warning } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerLogin = () => {
  const [mode, setMode] = useState('signin');
  const [username, setUsername] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [appName, setAppName] = useState('DWS Vault Chat');
  const [privacyText, setPrivacyText] = useState('');
  const [termsText, setTermsText] = useState('');
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showThreeDotMenu, setShowThreeDotMenu] = useState(false);
  
  const [supportPhone, setSupportPhone] = useState('');
  const [whatsappLink, setWhatsappLink] = useState('');
  const [telegramLink, setTelegramLink] = useState('');
  const [instagramLink, setInstagramLink] = useState('');
  
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [changePasswordData, setChangePasswordData] = useState({ current: '', new_pass: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);
  
  const { login, user } = useAuth();
  const navigate = useNavigate();

  const getWhatsappUrl = (wa) => {
    if (!wa) return '#';
    if (wa.startsWith('http://') || wa.startsWith('https://')) return wa;
    const digits = wa.replace(/\D/g, '');
    return digits ? `https://wa.me/${digits}` : '#';
  };

  const getTelegramUrl = (tg) => {
    if (!tg) return '#';
    if (tg.startsWith('http://') || tg.startsWith('https://')) return tg;
    return `https://t.me/${tg.replace(/^@/, '')}`;
  };

  const getInstagramUrl = (ig) => {
    if (!ig) return '#';
    if (ig.startsWith('http://') || ig.startsWith('https://')) return ig;
    return `https://instagram.com/${ig.replace(/^@/, '')}`;
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings?t=${Date.now()}`);
        const data = response.data || {};
        if (data.app_name) {
          setAppName(data.app_name);
          document.title = data.app_name;
        }
        setPrivacyText(data.privacy_policy_text || 'Privacy Policy content not available.');
        setTermsText(data.terms_conditions_text || 'Terms & Conditions content not available.');
        setSupportPhone(data.support_phone || data.phone || '');
        setWhatsappLink(data.whatsapp_link || data.whatsapp || '');
        setTelegramLink(data.telegram_link || '');
        setInstagramLink(data.instagram_link || '');
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    if (user && user.role === 'customer' && !user.is_guest) {
      navigate('/customer-select', { replace: true });
    }
  }, [user, navigate]);

  const handleTroubleSignIn = async () => {
    // Don't create user in DB yet - wait for first message
    let deviceId = localStorage.getItem('dws_device_id');
    if (!deviceId) {
      deviceId = 'dev_' + Math.random().toString(36).substr(2, 9) + Date.now();
      localStorage.setItem('dws_device_id', deviceId);
    }
    const pendingGuestUser = {
      id: null,
      username: 'Support Guest',
      role: 'customer',
      is_guest: true,
      is_pending: true,
      is_support_guest: true,
      device_id: deviceId
    };
    login(pendingGuestUser);
    navigate('/customer-chat?type=support&guest=true&pending=true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (mode === 'signup' && !username.trim()) { setError('Username is required'); return; }
    if (!phone.trim() || !password.trim()) { setError('Please fill in all fields'); return; }
    if (phone.replace(/\D/g, '').length !== 10) { setError('Mobile number must be exactly 10 digits'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      if (mode === 'signin') {
        const response = await axios.post(`${API}/auth/login`, { phone: phone.trim(), password: password.trim() });
        if (response.data && response.data.role === 'customer') {
          if (response.data.must_change_password) { setLoggedInUser(response.data); setShowPasswordChange(true); setLoading(false); return; }
          login(response.data);
          try { await axios.post(`${API}/admin/activity/track`, { user_id: response.data.id, action: 'login' }); } catch {}
          navigate('/customer-select', { replace: true });
        } else { setError('Invalid customer account'); }
      } else {
        const registrationIp = await fetch('https://api.ipify.org?format=json').then(r => r.json()).then(d => d.ip).catch(() => 'unknown');
        const deviceId = localStorage.getItem('dws_device_id') || '';
        const response = await axios.post(`${API}/auth/signup`, { username: username.trim(), phone: phone.trim(), password: password.trim(), role: 'customer', registration_ip: registrationIp, device_id: deviceId });
        if (response.data && response.data.role === 'customer') {
          login(response.data);
          try { await axios.post(`${API}/admin/activity/track`, { user_id: response.data.id, action: 'login' }); } catch {}
          navigate('/customer-select', { replace: true });
        } else { setError('Registration failed'); }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'An error occurred';
      setError(errorMsg.includes('Invalid credentials') ? (mode === 'signin' ? 'Incorrect mobile number or password' : 'Account already exists. Use Sign In.') : errorMsg);
    } finally { setLoading(false); }
  };

  if (user && user.role === 'customer' && !user.is_guest) {
    return <Navigate to="/customer-select" replace />;
  }

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex flex-col overflow-y-auto" style={{ height: '100vh', WebkitOverflowScrolling: 'touch' }}>
      
      {/* Three Dot Menu */}
      <div className="fixed top-3 right-3 z-30">
        <button onClick={() => setShowThreeDotMenu(!showThreeDotMenu)} className="p-2 bg-white/80 backdrop-blur rounded-full shadow-md hover:shadow-lg" data-testid="three-dot-menu">
          <DotsThreeVertical size={22} weight="bold" className="text-gray-600" />
        </button>
        {showThreeDotMenu && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setShowThreeDotMenu(false)} />
            <div className="absolute right-0 top-11 bg-white rounded-xl shadow-2xl py-2 w-52 z-50 border">
              <button onClick={() => { setShowThreeDotMenu(false); supportPhone ? (window.location.href = `tel:${supportPhone}`) : toast.error('Not configured'); }} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 text-left" data-testid="menu-call-support">
                <PhoneCall size={18} className="text-green-600" weight="fill" />
                <span className="text-sm text-gray-700">Call Support</span>
              </button>
              <button onClick={() => { setShowThreeDotMenu(false); whatsappLink ? window.open(whatsappLink, '_blank') : toast.error('Not configured'); }} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 text-left" data-testid="menu-whatsapp">
                <ChatCircle size={18} className="text-green-500" weight="fill" />
                <span className="text-sm text-gray-700">WhatsApp Support</span>
              </button>
              <div className="border-t my-1" />
              <button onClick={() => { setShowThreeDotMenu(false); handleTroubleSignIn(); }} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-wa-hover text-left" data-testid="menu-unable-signin">
                <Warning size={18} className="text-orange-500" weight="fill" />
                <span className="text-sm text-orange-600 font-medium">Unable to Sign In</span>
              </button>
            </div>
          </>
        )}
      </div>

      <div className="flex flex-col pb-8">
        {/* Login Form with Floating Icons */}
        <div className="flex items-center justify-center p-4 pt-8 pb-6 relative">
          
          {/* Left Floating Icons */}
          <div className="hidden md:flex flex-col items-center gap-4 absolute left-8" style={{ top: '50%', transform: 'translateY(-50%)' }}>
            <span className="text-sm font-bold bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text text-transparent tracking-wide">Join Us</span>
            <a href={getTelegramUrl(telegramLink)} target="_blank" rel="noopener noreferrer" onClick={(e) => { if (!telegramLink) e.preventDefault(); }} className="flex flex-col items-center gap-1 group" style={{ animation: 'floatUp 3s ease-in-out infinite' }} data-testid="floating-telegram">
              <div className="w-14 h-14 rounded-full bg-white shadow-lg flex items-center justify-center transition-transform group-hover:scale-110">
                <img src="https://cdn-icons-png.flaticon.com/512/2111/2111646.png" alt="Telegram" className="w-8 h-8" />
              </div>
              <span className="text-[10px] text-gray-500 font-medium">Telegram</span>
            </a>
            <a href={getInstagramUrl(instagramLink)} target="_blank" rel="noopener noreferrer" onClick={(e) => { if (!instagramLink) e.preventDefault(); }} className="flex flex-col items-center gap-1 group" style={{ animation: 'floatDown 3s ease-in-out infinite' }} data-testid="floating-instagram">
              <div className="w-14 h-14 rounded-full bg-white shadow-lg flex items-center justify-center transition-transform group-hover:scale-110">
                <img src="https://cdn-icons-png.flaticon.com/512/174/174855.png" alt="Instagram" className="w-8 h-8" />
              </div>
              <span className="text-[10px] text-gray-500 font-medium">Instagram</span>
            </a>
          </div>
          
          {/* Right Floating Icons */}
          <div className="hidden md:flex flex-col items-center gap-4 absolute right-8" style={{ top: '50%', transform: 'translateY(-50%)' }}>
            <span className="text-sm font-bold bg-gradient-to-r from-green-500 via-teal-500 to-blue-500 bg-clip-text text-transparent tracking-wide">Join Us</span>
            <a href={getWhatsappUrl(whatsappLink)} target="_blank" rel="noopener noreferrer" onClick={(e) => { if (!whatsappLink) e.preventDefault(); }} className="flex flex-col items-center gap-1 group" style={{ animation: 'floatDown 3s ease-in-out infinite' }} data-testid="floating-whatsapp">
              <div className="w-14 h-14 rounded-full bg-white shadow-lg flex items-center justify-center transition-transform group-hover:scale-110">
                <img src="https://cdn-icons-png.flaticon.com/512/733/733585.png" alt="WhatsApp" className="w-8 h-8" />
              </div>
              <span className="text-[10px] text-gray-500 font-medium">WhatsApp</span>
            </a>
            <a href={supportPhone ? `tel:${supportPhone}` : '#'} onClick={(e) => { if (!supportPhone) e.preventDefault(); }} className="flex flex-col items-center gap-1 group" style={{ animation: 'floatUp 3s ease-in-out infinite' }} data-testid="floating-call">
              <div className="w-14 h-14 rounded-full bg-white shadow-lg flex items-center justify-center transition-transform group-hover:scale-110">
                <img src="https://cdn-icons-png.flaticon.com/512/455/455705.png" alt="Call" className="w-8 h-8" />
              </div>
              <span className="text-[10px] text-gray-500 font-medium">Call Support</span>
            </a>
          </div>

          {/* Mobile Floating Icons (small, horizontal) */}
          <div className="flex md:hidden flex-col items-center gap-1.5 absolute top-1 left-0 right-0 z-10">
            <span className="text-xs font-bold bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">Join Us</span>
            <div className="flex gap-3">
            <a href={getTelegramUrl(telegramLink)} target="_blank" rel="noopener noreferrer" onClick={(e) => { if (!telegramLink) e.preventDefault(); }} className="w-9 h-9 rounded-full bg-white shadow flex items-center justify-center">
              <img src="https://cdn-icons-png.flaticon.com/512/2111/2111646.png" alt="TG" className="w-5 h-5" />
            </a>
            <a href={getInstagramUrl(instagramLink)} target="_blank" rel="noopener noreferrer" onClick={(e) => { if (!instagramLink) e.preventDefault(); }} className="w-9 h-9 rounded-full bg-white shadow flex items-center justify-center">
              <img src="https://cdn-icons-png.flaticon.com/512/174/174855.png" alt="IG" className="w-5 h-5" />
            </a>
            <a href={getWhatsappUrl(whatsappLink)} target="_blank" rel="noopener noreferrer" onClick={(e) => { if (!whatsappLink) e.preventDefault(); }} className="w-9 h-9 rounded-full bg-white shadow flex items-center justify-center">
              <img src="https://cdn-icons-png.flaticon.com/512/733/733585.png" alt="WA" className="w-5 h-5" />
            </a>
            <a href={supportPhone ? `tel:${supportPhone}` : '#'} onClick={(e) => { if (!supportPhone) e.preventDefault(); }} className="w-9 h-9 rounded-full bg-white shadow flex items-center justify-center">
              <img src="https://cdn-icons-png.flaticon.com/512/455/455705.png" alt="Call" className="w-5 h-5" />
            </a>
            </div>
          </div>

          {/* Login Card */}
          <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 w-full max-w-md border border-gray-200 z-10 mt-10 md:mt-0">
            <div className="text-center mb-5">
              <div className="w-24 h-24 mx-auto mb-3 flex items-center justify-center">
                <img src="/whatsapp.png" alt="WhatsApp" className="w-20 h-20 md:w-24 md:h-24 object-contain" />
              </div>
              <h2 className="text-xl font-bold text-gray-800">Customer Login</h2>
            </div>
            <div className="flex gap-2 mb-5 bg-gray-100 p-1 rounded-lg">
              <button type="button" onClick={() => { setMode('signin'); setError(''); setUsername(''); }} className={`flex-1 py-2.5 rounded-md font-semibold transition-all ${mode === 'signin' ? 'bg-blue-500 text-white shadow-md' : 'text-gray-600'}`}>Sign In</button>
              <button type="button" onClick={() => { setMode('signup'); setError(''); }} className={`flex-1 py-2.5 rounded-md font-semibold transition-all ${mode === 'signup' ? 'bg-blue-500 text-white shadow-md' : 'text-gray-600'}`}>Sign Up</button>
            </div>
            {error && <div className="mb-4 p-3 bg-wa-hover border border-red-300 text-wa-text-primary rounded-lg text-sm">{error}</div>}
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'signup' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                  <div className="relative"><input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 placeholder-gray-400" placeholder="Enter username" disabled={loading} /><User size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /></div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number</label>
                <div className="relative"><input type="tel" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} maxLength={10} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 placeholder-gray-400" placeholder="Enter 10 digit mobile number" disabled={loading} /><Phone size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /></div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <div className="relative"><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 placeholder-gray-400" placeholder="Enter password" disabled={loading} /><Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /></div>
              </div>
              <button type="submit" disabled={loading} className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50">
                {loading ? 'Processing...' : mode === 'signin' ? 'Sign In' : 'Sign Up'}
              </button>
            </form>
            {mode === 'signin' && (
              <p className="text-center text-sm mt-4">
                <button type="button" onClick={handleTroubleSignIn} disabled={loading} className="text-orange-500 hover:text-orange-600 font-semibold" data-testid="trouble-signin-btn">Trouble Signing In?</button>
              </p>
            )}
          </div>
        </div>

        {/* Footer Links */}
        <div className="bg-white border-t border-gray-200 py-6">
          <div className="max-w-4xl mx-auto px-4 flex flex-wrap justify-center items-center gap-4 md:gap-8 text-sm">
            <button onClick={() => setShowPrivacyModal(true)} className="text-blue-600 hover:text-wa-text-primary font-medium">Privacy Policy</button>
            <span className="text-gray-300">|</span>
            <button onClick={() => setShowTermsModal(true)} className="text-blue-600 hover:text-wa-text-primary font-medium">Terms & Conditions</button>
          </div>
        </div>

        {/* Full Logo Section (restored) */}
        <div className="w-full bg-gradient-to-b from-white to-slate-50 py-12 md:py-16 flex flex-col items-center justify-center border-t border-gray-200 shadow-lg flex-shrink-0 min-h-[45vh]">
          <p className="text-xs md:text-sm font-semibold text-gray-400 uppercase tracking-widest mb-3">Powered by</p>
          <img src="https://customer-assets.emergentagent.com/job_project-deploy-12/artifacts/wr3xvidv_1000485427.jpg" alt={appName} className="w-full h-auto object-contain max-h-[280px]" />
        </div>

        {/* Welcome Text Section (restored) */}
        <div className="bg-gradient-to-b from-slate-50 to-slate-100 py-8 px-4 border-t border-gray-200">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-gray-600 text-xs md:text-sm leading-relaxed">
              Welcome to <span className="font-semibold text-blue-600">{appName}</span>, the official high-security platform for our gaming community. 
              This application provides a direct and encrypted channel for quick, reliable support. 
              Here, you can instantly communicate with our team for secure withdrawal and deposit processing. 
              Resolve account issues, get real-time assistance, and receive all your support needs with the highest level of professional care and security. 
              Our team is dedicated to providing you with a seamless and trusted banking and support experience.
            </p>
          </div>
        </div>
      </div>

      {/* Float animations */}
      <style>{`
        @keyframes floatUp { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        @keyframes floatDown { 0%,100% { transform: translateY(0); } 50% { transform: translateY(10px); } }
      `}</style>
    </div>

    {/* Privacy Modal */}
    {showPrivacyModal && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col">
          <div className="flex items-center justify-between p-5 border-b"><h2 className="text-lg font-bold">Privacy Policy</h2><button onClick={() => setShowPrivacyModal(false)} className="p-2 hover:bg-gray-100 rounded-full"><X size={22} /></button></div>
          <div className="p-5 overflow-y-auto flex-1"><div className="text-gray-700 whitespace-pre-wrap leading-relaxed" dangerouslySetInnerHTML={{ __html: privacyText }} /></div>
        </div>
      </div>
    )}

    {/* Terms Modal */}
    {showTermsModal && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col">
          <div className="flex items-center justify-between p-5 border-b"><h2 className="text-lg font-bold">Terms & Conditions</h2><button onClick={() => setShowTermsModal(false)} className="p-2 hover:bg-gray-100 rounded-full"><X size={22} /></button></div>
          <div className="p-5 overflow-y-auto flex-1"><div className="text-gray-700 whitespace-pre-wrap leading-relaxed" dangerouslySetInnerHTML={{ __html: termsText }} /></div>
        </div>
      </div>
    )}

    {/* Password Change Modal */}
    {showPasswordChange && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl" data-testid="change-password-modal">
          <h3 className="text-lg font-bold text-gray-800 mb-2">Change Password</h3>
          <p className="text-sm text-gray-500 mb-4">Please change your password for security.</p>
          <div className="space-y-3">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label><input type="password" value={changePasswordData.current} onChange={(e) => setChangePasswordData(p => ({ ...p, current: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white text-gray-900 placeholder-gray-400" placeholder="Enter current password" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">New Password</label><input type="password" value={changePasswordData.new_pass} onChange={(e) => setChangePasswordData(p => ({ ...p, new_pass: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white text-gray-900 placeholder-gray-400" placeholder="Enter new password (min 6 chars)" /></div>
          </div>
          <div className="flex gap-3 mt-5">
            <button onClick={() => { if (loggedInUser) { login(loggedInUser); navigate('/customer-select', { replace: true }); } }} className="flex-1 px-4 py-2.5 border rounded-lg text-gray-700 hover:bg-gray-50">Skip</button>
            <button onClick={async () => {
              if (!changePasswordData.current || !changePasswordData.new_pass) { toast.error('Fill both fields'); return; }
              if (changePasswordData.new_pass.length < 6) { toast.error('Min 6 characters'); return; }
              setChangingPassword(true);
              try { await axios.post(`${API}/auth/change-password`, { user_id: loggedInUser.id, current_password: changePasswordData.current, new_password: changePasswordData.new_pass }); toast.success('Password changed!'); login({ ...loggedInUser, must_change_password: false }); navigate('/customer-select', { replace: true }); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
              setChangingPassword(false);
            }} disabled={changingPassword} className="flex-1 px-4 py-2.5 bg-[#00a884] text-white rounded-lg hover:bg-[#008f72] disabled:opacity-50">{changingPassword ? 'Changing...' : 'Change'}</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default CustomerLogin;
